import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Rocket, CheckCircle2, AlertTriangle, ShieldAlert, Clock, RefreshCw } from "lucide-react";

const TONE = {
    PASS:              { icon: CheckCircle2, cls: "text-emerald-800 bg-emerald-50 border-emerald-200" },
    WARNING:           { icon: AlertTriangle, cls: "text-amber-800 bg-amber-50 border-amber-200" },
    BLOCKED:           { icon: ShieldAlert,   cls: "text-rose-800 bg-rose-50 border-rose-200" },
    READY_FOR_REVIEW:  { icon: Clock,         cls: "text-blue-800 bg-blue-50 border-blue-200" },
    APPROVED:          { icon: CheckCircle2,  cls: "text-emerald-800 bg-emerald-50 border-emerald-200" },
    NOT_READY:         { icon: Clock,         cls: "text-stone-700 bg-stone-100 border-stone-200" },
};

function Pill({ status }) {
    const meta = TONE[status] || TONE.NOT_READY;
    const Icon = meta.icon;
    return (
        <span className={`inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.cls}`}>
            <Icon size={11} /> {status.replace(/_/g, " ")}
        </span>
    );
}

export default function LaunchReadiness() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [launchState, setLaunchState] = useState(null);
    const [monitor, setMonitor] = useState(null);
    const [flipBusy, setFlipBusy] = useState(false);
    const load = () => {
        setBusy(true);
        Promise.all([
            api.get("/launch-readiness/report").then(r => setData(r.data)),
            api.get("/launch-state/current").then(r => setLaunchState(r.data)).catch(() => setLaunchState(null)),
            api.get("/launch-state/monitor").then(r => setMonitor(r.data)).catch(() => setMonitor(null)),
        ]).finally(() => setBusy(false));
    };
    useEffect(() => { load(); }, []);

    const flipLive = async () => {
        const backup_id = window.prompt("Backup ID (rollback checkpoint):", launchState?.backup_id || "");
        if (!backup_id) return;
        const admin_approval = window.prompt("Admin approval note (your name + why this is safe to go LIVE):");
        if (!admin_approval) return;
        if (!window.confirm(`Flip launch_state → LIVE with backup ${backup_id}?\nThis is recorded permanently.`)) return;
        setFlipBusy(true);
        try {
            await api.post("/launch-state/flip", { backup_id, admin_approval, status: "LIVE" });
            toast.success("Launch state flipped to LIVE");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't flip launch state");
        } finally { setFlipBusy(false); }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="launch-readiness-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <Rocket size={12} /> Growth · Launch Centre
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">
                        VAULT 2.0 launch readiness.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Every section is composed from real system state — feature flags, audit events,
                        testimonials, household ownership labels and the demo tenant. READY is a human
                        decision; this page never approves a launch on its own.
                    </p>
                </div>
                <button onClick={load} disabled={busy}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold border border-stone-300 rounded-full px-3.5 py-2 hover:border-stone-500"
                        data-testid="lr-refresh">
                    <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {data && (
                <>
                    <div className="rounded-2xl border border-stone-200 bg-white p-5 mb-4" data-testid="lr-overall">
                        <div className="flex items-center flex-wrap gap-3">
                            <div>
                                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Overall</div>
                                <div className="text-2xl font-display text-stone-900 mt-1">
                                    <Pill status={data.overall_status} />
                                </div>
                            </div>
                            <div className="flex gap-6 ml-auto text-[12.5px]">
                                <div><div className="text-stone-500">Critical</div><div className="text-xl font-display tabular-nums text-rose-700">{data.counts.critical}</div></div>
                                <div><div className="text-stone-500">Warnings</div><div className="text-xl font-display tabular-nums text-amber-700">{data.counts.warnings}</div></div>
                                <div><div className="text-stone-500">Categories</div><div className="text-xl font-display tabular-nums text-stone-900">{data.counts.categories}</div></div>
                            </div>
                        </div>
                        <div className="text-[11.5px] text-stone-500 mt-3">{data.note}</div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(data.sections).map(([name, sec]) => (
                            <div key={name} className="rounded-xl border border-stone-200 bg-white p-4"
                                 data-testid={`lr-section-${name.toLowerCase()}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="text-[13px] font-semibold text-stone-900">{name}</div>
                                    <div className="ml-auto"><Pill status={sec.status} /></div>
                                </div>
                                {sec.critical.length > 0 && (
                                    <ul className="space-y-1 mb-2">
                                        {sec.critical.map((c, i) => (
                                            <li key={i} className="text-[12px] text-rose-800 flex items-start gap-1.5">
                                                <ShieldAlert size={11} className="mt-0.5 shrink-0" /> {c}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {sec.warnings.length > 0 && (
                                    <ul className="space-y-1 mb-2">
                                        {sec.warnings.map((w, i) => (
                                            <li key={i} className="text-[12px] text-amber-800 flex items-start gap-1.5">
                                                <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {w}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {sec.notes.length > 0 && (
                                    <ul className="space-y-0.5">
                                        {sec.notes.map((n, i) => (
                                            <li key={i} className="text-[11.5px] text-stone-500">{n}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 rounded-xl border border-dashed border-stone-300 bg-stone-50 p-4">
                        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Release checklist</div>
                        <ol className="list-decimal list-inside text-[12.5px] text-stone-700 space-y-1">
                            <li>Development → Staging</li>
                            <li>Admin Review — this page must show 0 Critical</li>
                            <li>Backup — trigger a data snapshot from Settings → Data Export</li>
                            <li>Production — deploy</li>
                            <li>Smoke Test — walk through /product, /together, /demo, sign-up</li>
                            <li>Monitor — first 7 days of signup errors, checkout errors, invitations, demo, security events</li>
                        </ol>
                        <div className="text-[11px] text-stone-500 mt-2">
                            Create a rollback point immediately before release.
                        </div>
                    </div>

                    {launchState && (
                        <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-5" data-testid="launch-state-card">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Launch state</div>
                                <Pill status={launchState.status || "NOT_READY"} />
                                {launchState.backup_id && <span className="ml-auto text-[11px] font-mono text-stone-500">{launchState.backup_id}</span>}
                            </div>
                            {launchState.admin_approver_email && (
                                <div className="text-[11.5px] text-stone-500 mb-2">Last approved by <strong>{launchState.admin_approver_email}</strong> at {(launchState.recorded_at || "").slice(0, 19)}</div>
                            )}
                            <div className="flex gap-2 flex-wrap">
                                <button onClick={flipLive} disabled={flipBusy || launchState.status === "LIVE"}
                                        className="text-[12.5px] font-semibold text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 rounded-full px-4 py-1.5"
                                        data-testid="launch-flip-live">
                                    {launchState.status === "LIVE" ? "Already LIVE" : (flipBusy ? "Flipping…" : "Flip to LIVE")}
                                </button>
                            </div>
                        </div>
                    )}

                    {monitor && (
                        <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-5" data-testid="launch-monitor-card">
                            <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Soft-launch watch · 24 h</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {[
                                    ["authentication_failures", "Auth failures"],
                                    ["server_errors_5xx",       "5xx errors"],
                                    ["webhook_failures",        "Webhook failures"],
                                    ["permission_denials",      "Permission denials"],
                                    ["email_failures",          "Email failures"],
                                    ["notification_failures",   "Notification failures"],
                                ].map(([k, l]) => (
                                    <div key={k} className="border border-stone-200 rounded-lg p-2.5" data-testid={`monitor-${k}`}>
                                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">{l}</div>
                                        <div className={`text-2xl font-display tabular-nums mt-0.5 ${monitor[k] > 0 ? "text-rose-700" : "text-emerald-800"}`}>{monitor[k]}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="text-[11px] text-stone-500 mt-2">{monitor.note}</div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
